/**
 * TrendDataTable · Module A 投放趋势对比 - 数据表
 *
 * 表格视图：
 * - 行：游戏（icon + 名称）
 * - 列：每个时间点 + 总计
 * - 单元格：DBO_V.reviews 缩写数值
 *
 * ⚠️ 数据契约：series 仅 game_id；游戏元信息从 gameMap 关联
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_V, DBO_TM } from '@op/utils/format';
import { ITrendSeries } from '../../types/trend';
import { TGameMap } from '../../hooks/useGameMap';
import GameCell from '../KpiOverview/GameCell';
import { IFilter } from '../../types';

export interface ITrendDataTableProps {
  series: ITrendSeries[];
  gameMap: TGameMap;
  height?: number;
  filter: IFilter;
  emptyTitleText: string;
}

const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 56;
const MIN_HEIGHT = 280;

interface ITableRow {
  game_id: string;
  total: number;
  /** 各日期点的值，key = 日期字符串 */
  [date: string]: any;
}

export default function TrendDataTable({
  series,
  gameMap,
  height,
  filter,
  emptyTitleText
}: ITrendDataTableProps) {

  const empty = useMemo(() => {
    return !series?.length || series.every((item) => !item.values?.length);
  }, [series]);

  const { records, dates } = useMemo(() => {
    const dateList = series[0]?.values.map((v) => v.date) ?? [];
    const rows: ITableRow[] = series.map((s) => {
      const row: ITableRow = { game_id: s.game_id, total: 0 };
      let sum = 0;
      s.values.forEach((point) => {
        const v = Number.isFinite(point.value) ? point.value : 0;
        row[point.date] = v;
        sum += v;
      });
      row.total = sum;
      return row;
    });
    return { records: rows, dates: dateList };
  }, [series]);

  const columns = useMemo(
    () => [
      {
        key: 'game',
        header: 'Game',
        width: 220,
        fixed: 'left' as const,
        render: (row: ITableRow) => {
          const game = gameMap[row.game_id];
          return (
            <GameCell
              gameName={game?.game_name || row.game_id}
              cover={game?.cover}
            />
          );
        },
      },
      ...dates.map((date) => ({
        key: date,
        header: DBO_TM.formatFunc(filter?.date_type || 'daily')(date),
        align: 'right' as const,
        width: 130,
        render: (row: ITableRow) =>
          row[date] > 0 ? DBO_V.reviews(row[date]) : '-',
      })),
    ],
    [dates, gameMap],
  );

  const tableHeight = useMemo(
    () => height ?? Math.max(MIN_HEIGHT, HEADER_HEIGHT + records.length * ROW_HEIGHT),
    [height, records.length],
  );

  const recordKey = useMemo(() => (r: ITableRow) => r.game_id, []);

  return (
    <TableCommonHook
      records={empty ? [] : records}
      total={records.length}
      columns={columns}
      recordKey={recordKey}
      hasPageable={false}
      maxHeight={tableHeight}
      emptyProps={{ emptyTitleText, hasDesc: false }}
      emptyHeight={tableHeight}
      bordered={'all'}
    />
  );
}
